(() => {
  if (window.CaffreyInsoleGallery) return;
  const labels = document.currentScript?.dataset || {};
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const instances = new Map();
  const configurations = [
    {root: '[data-z-gallery]', stage: '.cf-z-slides', panel: '[data-z-slide]', item: '[data-z-dot]', prev: '[data-z-prev]', next: '[data-z-next]'},
    {root: '[data-sa-gallery]', panel: '[data-sa-slide]', item: '[data-sa-dot]', prev: '[data-sa-prev]', next: '[data-sa-next]'},
    {root: '.cf-sprint-gallery', panel: '[data-sprint-slide]', item: '[data-sprint-dot]', prev: '[data-sprint-prev]', next: '[data-sprint-next]'},
    {root: '[data-sn-gallery]', stage: '.cf-sn-hero-image', item: '[data-sn-image]', prev: '[data-sn-prev]', next: '[data-sn-next]'},
    {root: '.vs-hero-media', stage: '.vs-gallery-stage', item: '[data-vs-view]', prev: '[data-vs-step="-1"]', next: '[data-vs-step="1"]'},
    {root: '.cf-en-gallery', stage: '[data-noire-frame]', item: '[data-noire-thumb]', prev: '[data-cf-gallery-prev]', next: '[data-cf-gallery-next]'}
  ];
  const nativeRoots = 'cf-arp2-gallery,cf-max-gallery,cf-mst2-gallery,cf-os2-gallery,cf-ghp-gallery,cf-woa-gallery';
  function activeIndex(items) {
    return Math.max(0, items.findIndex(item => item.getAttribute('aria-current') === 'true' || item.getAttribute('aria-pressed') === 'true'));
  }
  function gestures(stage, move, signal, mouseOnly = false) {
    let drag = null, suppressUntil = 0;
    stage.addEventListener('pointerdown', event => {
      if (!event.isPrimary || event.button !== 0 || mouseOnly && event.pointerType !== 'mouse') return;
      if (event.target.closest('button,input,select,textarea')) return;
      drag = {id: event.pointerId, x: event.clientX, y: event.clientY};
      stage.setPointerCapture(event.pointerId);
    }, {signal});
    stage.addEventListener('pointerup', event => {
      if (!drag || drag.id !== event.pointerId) return;
      const dx = event.clientX - drag.x, dy = event.clientY - drag.y;
      drag = null;
      if (Math.abs(dx) < Math.max(30, stage.clientWidth * .08) || Math.abs(dx) <= Math.abs(dy) * 1.2) return;
      suppressUntil = performance.now() + 450;
      move(dx < 0 ? 1 : -1);
    }, {signal});
    stage.addEventListener('pointercancel', () => { drag = null; }, {signal});
    stage.addEventListener('lostpointercapture', () => { drag = null; }, {signal});
    stage.addEventListener('dragstart', event => event.preventDefault(), {signal});
    stage.addEventListener('click', event => {
      if (event.isTrusted && performance.now() < suppressUntil) {
        event.preventDefault(); event.stopImmediatePropagation();
      }
    }, {signal, capture: true});
  }
  function mount(root, config) {
    if (instances.has(root)) return;
    const items = [...root.querySelectorAll(config.item)];
    if (items.length < 2) return;
    const stage = config.stage ? root.querySelector(config.stage) : root;
    if (!stage) return;
    const abort = new AbortController(), signal = abort.signal;
    let serial = 0, outgoing = null, animations = [], hiddenPanel = null, routing = false;
    const panel = () => config.panel
      ? [...root.querySelectorAll(config.panel)].find(node => !node.hidden && !node.hasAttribute('data-cf-gallery-outgoing'))
      : stage.querySelector('img:not([data-cf-gallery-outgoing])');
    stage.classList.add('cf-insole-gallery-stage');
    if (!stage.hasAttribute('tabindex')) stage.tabIndex = 0;
    let count = null;
    if (!root.querySelector(config.next)) {
      const controls = document.createElement('div');
      controls.className = 'cf-insole-gallery-controls';
      for (const [direction, symbol] of [['prev', '←'], ['next', '→']]) {
        const button = document.createElement('button');
        button.type = 'button'; button.dataset['cfGallery' + (direction === 'prev' ? 'Prev' : 'Next')] = '';
        button.textContent = symbol;
        button.setAttribute('aria-label', direction === 'prev' ? labels.previous : labels.next);
        button.addEventListener('click', () => {
          routing = true;
          items[(activeIndex(items) + (direction === 'prev' ? -1 : 1) + items.length) % items.length].click();
          routing = false;
        }, {signal});
        controls.append(button);
      }
      count = document.createElement('span');
      count.setAttribute('aria-live', 'polite'); count.setAttribute('aria-atomic', 'true');
      controls.insertBefore(count, controls.lastChild);
      root.append(controls);
    }
    const updateCount = () => { if (count) count.textContent = `${activeIndex(items) + 1} / ${items.length}`; };
    function clear() {
      animations.forEach(animation => animation.cancel()); animations = [];
      if (hiddenPanel) hiddenPanel.style.visibility = '';
      hiddenPanel = null; outgoing?.remove(); outgoing = null;
    }
    function transition(direction) {
      const token = ++serial;
      clear();
      const old = panel();
      if (!old || motion.matches || typeof old.animate !== 'function') return;
      const rect = old.getBoundingClientRect(), bounds = stage.getBoundingClientRect();
      outgoing = old.cloneNode(true);
      const all = [outgoing, ...outgoing.querySelectorAll('*')];
      all.forEach(node => {
        [...node.attributes].forEach(attr => { if (attr.name === 'id' || attr.name === 'name' || attr.name.startsWith('data-')) node.removeAttribute(attr.name); });
        if (node.tagName === 'IMG') {
          const original = node === outgoing ? old : [...old.querySelectorAll('img')][[...outgoing.querySelectorAll('img')].indexOf(node)];
          if (original) node.src = original.currentSrc || original.src;
          node.removeAttribute('srcset'); node.removeAttribute('loading');
          node.alt = '';
        }
      });
      outgoing.removeAttribute('hidden'); outgoing.setAttribute('aria-hidden', 'true'); outgoing.inert = true;
      outgoing.setAttribute('data-cf-gallery-outgoing', '');
      Object.assign(outgoing.style, {position: 'absolute', left: `${rect.left - bounds.left - stage.clientLeft + stage.scrollLeft}px`, top: `${rect.top - bounds.top - stage.clientTop + stage.scrollTop}px`, width: `${rect.width}px`, height: `${rect.height}px`, maxWidth: 'none', margin: '0', zIndex: '2', pointerEvents: 'none', visibility: 'visible'});
      stage.append(outgoing);
      setTimeout(async () => {
        if (token !== serial || !root.isConnected) return;
        const next = panel();
        if (!next) { clear(); return; }
        hiddenPanel = next; next.style.visibility = 'hidden';
        const images = next.tagName === 'IMG' ? [next] : [...next.querySelectorAll('img')];
        await Promise.all(images.map(img => img.decode().catch(() => {})));
        if (token !== serial || !root.isConnected) return;
        next.style.visibility = ''; hiddenPanel = null;
        const base = getComputedStyle(next).transform;
        const transform = base === 'none' ? '' : ` ${base}`;
        const timing = {duration: 320, easing: 'cubic-bezier(.22,.61,.36,1)', fill: 'both'};
        animations = [next.animate([{transform: `translateX(${direction * 100}%)${transform}`}, {transform: `translateX(0)${transform}`}], timing), outgoing.animate([{transform: 'translateX(0)'}, {transform: `translateX(${-direction * 100}%)`}], timing)];
        await Promise.all(animations.map(animation => animation.finished.catch(() => {})));
        if (token === serial) clear();
      }, 0);
    }
    root.addEventListener('click', event => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
      const target = event.target.closest(config.item);
      const current = activeIndex(items), chosen = items.indexOf(target);
      let direction = 0;
      if (event.target.closest(config.next)) direction = 1;
      else if (event.target.closest(config.prev)) direction = -1;
      else if (chosen >= 0 && chosen !== current) direction = chosen > current ? 1 : -1;
      if (direction && !routing) transition(direction);
      setTimeout(updateCount, 0);
    }, {capture: true, signal});
    const move = step => root.querySelector(step > 0 ? config.next : config.prev)?.click();
    gestures(stage, move, signal);
    stage.addEventListener('keydown', event => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key) || event.target.closest('input,select,textarea')) return;
      event.preventDefault(); event.stopImmediatePropagation(); move(event.key === 'ArrowRight' ? 1 : -1);
    }, {capture: true, signal});
    root.dataset.cfSwipeReady = 'animated';
    updateCount();
    instances.set(root, () => { serial++; clear(); abort.abort(); });
  }
  function native(root) {
    if (instances.has(root)) return;
    const stage = root.querySelector('[class*="slides"],[class*="track"]');
    if (!stage || stage.querySelectorAll('img').length < 2) return;
    const abort = new AbortController();
    gestures(stage, step => {
      const buttons = [...root.querySelectorAll('button')];
      const target = buttons.find(button => [...button.attributes].some(attr => attr.name.endsWith(step > 0 ? '-next' : '-prev')));
      target?.click();
    }, abort.signal, true);
    root.dataset.cfSwipeReady = 'native';
    instances.set(root, () => abort.abort());
  }
  function refresh(scope = document) {
    configurations.forEach(config => {
      if (scope.matches?.(config.root)) mount(scope, config);
      scope.querySelectorAll(config.root).forEach(root => mount(root, config));
    });
    scope.querySelectorAll(nativeRoots).forEach(native);
    scope.querySelectorAll('media-gallery .product__media-list').forEach(stage => {
      if (stage.querySelectorAll('.product__media-item').length > 1) stage.dataset.cfSwipeNative = '';
    });
  }
  window.CaffreyInsoleGallery = {refresh};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => refresh(), {once: true});
  else refresh();
  document.addEventListener('shopify:section:load', event => refresh(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, cleanup] of instances) if (event.target.contains(root)) { cleanup(); instances.delete(root); }
  });
})();
